import UIStore from "../../store/uiStore";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Bot, KeyRound, Package, Plus } from "lucide-react";
import Header from "../../components/Header/Header";
import { PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import AgentMarketplaceSection from "../../components/Forge/AgentMarketplaceSection/AgentMarketplaceSection";
import ProviderConnectionsSection from "../../components/Forge/ProviderConnectionsSection/ProviderConnectionsSection";
import ForgeSkillsSection from "../../components/Forge/ForgeSkillsSection/ForgeSkillsSection";
import ROUTES from "../../router/routes";

const TABS = [
  { id: "agents", label: "Agents", icon: Bot },
  { id: "skills", label: "Skills", icon: Package },
  { id: "connections", label: "My Connections", icon: KeyRound },
];

const TAB_DESCRIPTION = {
  agents: "Discover agents available in your organization and connect the accounts they need.",
  skills: "Install and manage reusable instruction sets that extend agent capabilities.",
  connections: "Manage the personal accounts your agents use to access external services.",
};

const ForgeDashboard = () => {
  const isAdmin = UIStore((state) => state.orgAvailable) === true;
  const [isInstallPanelOpen, setIsInstallPanelOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab = TABS.some(({ id }) => id === requestedTab) ? requestedTab : "agents";

  const selectTab = (tabId) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("tab", tabId);
      return next;
    });
  };

  return (
    <div className="w-full">
      <Header type="forge" />
      <div className="h-[calc(100%-80px)] overflow-auto show-scrollbar">
        <div className="max-w-6xl px-4 sm:px-6 mx-auto pb-16">
          <div className="mt-5 sm:mt-7">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="font-semibold text-2xl text-ink-ink dark:text-ink-white">Forge</h1>
                <p className="text-ink-gray-500 dark:text-ink-gray-400 text-sm mt-2">
                  {TAB_DESCRIPTION[activeTab]}
                </p>
              </div>

              {isAdmin && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  {activeTab === "agents" && (
                  <PrimaryButton
                    onClick={() => navigate(ROUTES.FORGE.ADD_AGENT)}
                    className="flex items-center gap-1.5"
                  >
                    <Plus className="w-4 h-4" />
                    Add Agent
                  </PrimaryButton>
                  )}
                  {activeTab === "skills" && (
                    <PrimaryButton
                      onClick={() => setIsInstallPanelOpen(true)}
                      className="flex items-center gap-1.5"
                    >
                      <Plus className="w-4 h-4" />
                      Add Skills
                    </PrimaryButton>
                  )}
                </div>
              )}
            </div>

            <div className="mt-7 mb-6 border-b border-edge-gray-200 dark:border-edge-panel-border">
              <div className="flex items-end justify-between gap-4">
                <div className="flex items-center gap-6 overflow-x-auto">
                  {TABS.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => selectTab(id)}
                      className={`relative flex items-center gap-2 pb-3 text-sm font-medium whitespace-nowrap transition-colors ${
                        activeTab === id
                          ? "text-ink-primary"
                          : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-ink dark:hover:text-ink-white"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                      {activeTab === id && <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-fill-primary" />}
                    </button>
                  ))}
                </div>
                {isAdmin && activeTab === "connections" && (
                  <SecondaryButton
                    onClick={() => navigate(ROUTES.SETTINGS.FORGE_PROVIDERS)}
                    className="flex items-center gap-1.5 flex-shrink-0 mb-2"
                  >
                    <KeyRound className="w-4 h-4" />
                    Manage Providers
                  </SecondaryButton>
                )}
              </div>
            </div>

            {activeTab === "agents" && <AgentMarketplaceSection />}
            {activeTab === "skills" && (
              <ForgeSkillsSection
                showHeader={false}
                installPanelOpen={isInstallPanelOpen}
                onCloseInstallPanel={() => setIsInstallPanelOpen(false)}
              />
            )}
            {activeTab === "connections" && <ProviderConnectionsSection />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgeDashboard;
